import { Box, FormHelperText, Grid, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Theme } from '@mui/material/styles';
import { useSnackbar } from 'notistack';
import { FC, useState } from 'react';
import { Loader, ManyImagesUpload } from '../../../../components';
import { getNewsFile } from '../../../../fetch';
import { INewsDetail } from '../../../../models';
import { useQuery } from 'react-query';
import { ALLOWED_IMAGE_EXTENSIONS, ALLOWED_VIDEO_EXTENSIONS } from '../../../../helpers';

interface INewsFile {
  newsItemId: string | number;
  image?: string;
  imageTitle: string;
  imageData(value): void;
  currentNewsItem?: INewsDetail | null;
  file: any;
  setFile: React.Dispatch<React.SetStateAction<any>>;
  values: any;
  resetForm: (nextState: any) => void;
}

export const NewsFile: FC<INewsFile> = ({
  newsItemId,
  image,
  imageTitle,
  imageData,
  currentNewsItem,
  file,
  setFile,
  values,
  resetForm
}) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [isUploading, setUploading] = useState(false);
  const [fileName, setFileName] = useState('');

  // this will on be true if the news item type has changed from a file to something else and the item hasn't been saved yet
  // In this case, we want to update the file when we save the entire modal
  const hasChangedContentTypeToAttachedFile =
    currentNewsItem?.newsItemType !== 'AttachedFile' && values?.newsItemType === 'AttachedFile';

  const [fileType, setFileType] = useState(null);

  const getFileExtension = (fileName: string) => {
    return fileName?.split('.')?.pop();
  };

  const { isLoading: isLoadingFile } = useQuery(
    ['newsFile', newsItemId, values?.newsItemType],
    () => newsItemId && getNewsFile(newsItemId, true),
    {
      onSuccess: newsFile => {
        if (newsFile?.status !== 204 && newsFile?.status >= 200 && newsFile?.status < 300) {
          let url = URL.createObjectURL(newsFile?.data);

          const fileName = newsFile?.headers?.['content-disposition']
            ?.split('filename=')?.[1]
            ?.split(';')?.[0]
            ?.replaceAll('"', '');

          setFileName(fileName);
          setFileType(getFileExtension(fileName));
          imageData(url);

          if (hasChangedContentTypeToAttachedFile) {
            imageData(url);
          } else {
            resetForm({ values: { ...values, fileData: url } });
          }
        }
      },
      enabled: newsItemId && values?.newsItemType && values?.newsItemType === 'AttachedFile'
    }
  );

  const [uploadedFile, setUploadedFile] = useState(null);
  return (
    <>
      <Box className={classes.photosContainer}>
        <Box paddingRight="8px">
          <ManyImagesUpload
            // allow pdf, mp4, and image file types
            fileType={['.pdf']
              .concat(ALLOWED_IMAGE_EXTENSIONS)
              .concat(ALLOWED_VIDEO_EXTENSIONS)
              .join(', ')}
            imageTitle={imageTitle}
            isRequired={false}
            isDisabled={isUploading}
            isLoading={isUploading}
            imageKey={'newsFile'}
            uploadText={'Upload File'}
            handleFileChange={async (images: any[], files: File[]) => {
              try {
                setUploading(true);

                const formData = new FormData();
                const firstItem = files[0];
                setFileName(firstItem.name);

                formData.append('AttachedFile', firstItem);

                setFileType(getFileExtension(firstItem.name));
                let url = URL.createObjectURL(firstItem);
                setUploadedFile(url);
                imageData(formData);
              } catch (error) {
                enqueueSnackbar(error?.Detail ?? 'Error uploading image, please try again.', {
                  variant: 'error'
                });
              } finally {
                setUploading(false);
              }
            }}
            images={[]}
          />
        </Box>
      </Box>
      {ALLOWED_IMAGE_EXTENSIONS.includes(fileType) && (
        <Grid container spacing={0.25} mb={0.5} mt={0.5}>
          <Grid item xs={12}>
            <Box
              component="img"
              className={classes.imageFormat}
              alt="News Item"
              src={uploadedFile || image}
            />
          </Grid>
        </Grid>
      )}
      {/* Is a pdf file or not an image, so just show the file name */}
      {(fileType === 'pdf' || !ALLOWED_IMAGE_EXTENSIONS.includes(fileType)) && !newsItemId && (
        // Display PDF file name once uploaded before being saved
        <Grid container spacing={0.25} mb={0.5} mt={0.5}>
          <Grid item xs={12}>
            <Typography>{fileName}</Typography>
          </Grid>
        </Grid>
      )}
      <Grid container spacing={0.25} mb={0.5}>
        {isLoadingFile && (
          <Grid item xs={12}>
            <Loader type="inline" />
          </Grid>
        )}
        {image && newsItemId && !isLoadingFile && (
          <>
            <Grid item xs={12}>
              <a
                href={image}
                download={fileName || 'newsFile.pdf'}
                className={
                  newsItemId && image
                    ? classes.downloadText
                    : `${classes.downloadText} ${classes.disabledLink}`
                }
              >
                {image ? fileName || 'Download File' : 'No File Uploaded'}
              </a>
            </Grid>
          </>
        )}
      </Grid>
      {!values.fileData && !isLoadingFile && (
        <FormHelperText error>A file is required</FormHelperText>
      )}
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  photosContainer: {
    display: 'flex',
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column'
    },
    '@media print': {
      display: 'block'
    }
  },
  imageFormat: {
    width: '100%',
    height: 'auto',
    border: `2px solid ${theme.palette.grey[300]}`,
    borderRadius: theme.shape.borderRadius,
    boxShadow: '0 2px 3px rgba(0,0,0,0.06)'
  },
  downloadText: {
    color: theme.palette.primary.main,
    textDecorationColor: theme.palette.primary.main
  },
  disabledLink: {
    pointerEvents: 'none',
    opacity: 0.5
  }
}));
